import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Calendar, Phone } from 'lucide-react';
import { navLinks } from '../../data/mock';

const Header = ({ onOpenCalendar }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href) => {
    setIsOpen(false);
    if (href.includes('#')) {
      const id = href.split('#')[1];
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-[99999] transition-all duration-500 ${
        scrolled ? 'py-2' : 'py-3 md:py-5'
      }`}
      style={{ 
        background: scrolled ? 'rgba(250, 255, 238, 0.95)' : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none'
      }}
    >
      <div className="container">
        <nav 
          className={`flex items-center justify-between rounded-full px-4 md:px-8 py-3 transition-all duration-300 ${
            scrolled ? 'shadow-lg' : ''
          }`}
          style={{ 
            background: 'var(--brand-dark)',
          }}
        >
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2"
            style={{ textDecoration: 'none' }}
          >
            <img 
              src="/logo.png"
              alt="Zenova Growth Partners" 
              className="h-10 w-10 rounded-full object-cover"
              style={{ background: 'rgba(255,255,255,0.1)' }}
            />
            <span className="text-white font-bold text-xl hidden sm:block">
              Zenova Growth Partners
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                onClick={() => handleNavClick(link.href)}
                className={`text-white/80 hover:text-white px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 hover:bg-white/10 ${
                  location.pathname === link.href ? 'bg-white/10 text-white' : ''
                }`}
                style={{ textDecoration: 'none' }}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="tel:+919876543210"
              className="flex items-center gap-2 text-white/80 hover:text-white text-sm font-medium transition-colors"
              style={{ textDecoration: 'none' }}
            >
              <Phone size={16} />
              <span className="hidden xl:inline">+91 9899668555</span>
            </a>
            <button
              onClick={onOpenCalendar}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 hover:scale-105"
              style={{ 
                background: 'var(--brand-primary)', 
                color: 'var(--brand-dark)',
              }}
            >
              <Calendar size={16} />
              Book Demo
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden text-white p-2 rounded-xl hover:bg-white/10 transition-colors"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {/* Mobile Navigation */}
        <div 
          className={`lg:hidden mt-2 rounded-3xl overflow-hidden transition-all duration-300 ${
            isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
          }`}
          style={{ 
            background: 'var(--brand-dark)',
          }}
        >
          <div className="p-4">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                onClick={() => handleNavClick(link.href)}
                className="block text-white/80 hover:text-white px-4 py-3 rounded-xl text-base font-medium transition-colors hover:bg-white/10"
                style={{ textDecoration: 'none' }}
              >
                {link.label}
              </Link>
            ))}
            <div className="mt-4 pt-4 border-t border-white/10 space-y-3">
              <a
                href="tel:+919876543210"
                className="flex items-center gap-3 text-white/80 hover:text-white px-4 py-2 text-base"
                style={{ textDecoration: 'none' }}
              >
                <Phone size={18} />
                +91 98765 43210
              </a>
              <button
                onClick={() => { setIsOpen(false); onOpenCalendar(); }}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold transition-all duration-300"
                style={{ 
                  background: 'var(--brand-primary)', 
                  color: 'var(--brand-dark)',
                }}
              >
                <Calendar size={18} />
                Book Your Free Demo
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
